/**
 * Invoice Module TypeScript Interfaces
 * BuilderBel Construction Project Management System
 */

// ============================================================================
// ENUMS / UNION TYPES
// ============================================================================

export type InvoiceType = 'time_and_materials' | 'fixed_price' | 'service' | 'progress' | 'final';

export type InvoiceStatus =
  | 'draft'
  | 'sent'
  | 'viewed'
  | 'partially_paid'
  | 'paid'
  | 'overdue'
  | 'void'
  | 'cancelled';

export type PaymentTerms =
  | 'net_30'
  | 'net_45'
  | 'net_60'
  | 'due_on_receipt'
  | 'custom';

export type PaymentMethod = 'check' | 'ach' | 'wire' | 'credit_card' | 'cash' | 'other';

export type DiscountType = 'percentage' | 'fixed';

// ============================================================================
// SUB-DOCUMENT INTERFACES
// ============================================================================

export interface InvoiceLineItem {
  _id?: string;
  description: string;
  quantity: number;
  unit: string;
  unitRate: number;
  amount: number;
  costCodeId?: string;
  costCode?: string;
  taskId?: string;
  timesheetEntryIds?: string[];
  notes?: string;
}

export interface InvoicePayment {
  _id?: string;
  amount: number;
  paymentDate: string;
  paymentMethod: PaymentMethod;
  referenceNumber?: string;
  notes?: string;
  recordedBy?: string;
  recordedAt: string;
}

// ============================================================================
// MAIN INVOICE INTERFACE
// ============================================================================

export interface Invoice {
  _id: string;
  projectId: string;
  organizationId: string;

  invoiceNumber: string;
  invoiceType: InvoiceType;
  title?: string;

  // Client
  clientId?: string;
  clientName: string;
  clientEmail?: string;
  clientAddress?: string;
  clientPhone?: string;

  // Line items
  lineItems: InvoiceLineItem[];

  // Dates
  invoiceDate: string;
  dueDate?: string;
  paidDate?: string;

  // Financial
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  discount: number;
  discountType: DiscountType;
  discountAmount: number;
  total: number;
  amountPaid: number;
  balanceDue: number;
  currency: string;

  // Terms & notes
  paymentTerms: PaymentTerms;
  notes?: string;
  terms?: string;
  internalNotes?: string;

  // Status
  status: InvoiceStatus;
  sentAt?: string;
  sentTo?: string;
  sentBy?: string;
  viewedAt?: string;

  // Payments
  payments: InvoicePayment[];

  // Approval
  approvedBy?: string;
  approvedDate?: string;

  // Audit
  createdBy: string;
  updatedBy?: string;
  deletedAt?: string;
  deletedBy?: string;
  createdAt: string;
  updatedAt: string;

  // Virtuals
  isOverdue?: boolean;
  lineItemsCount?: number;
}

// ============================================================================
// CREATE / UPDATE REQUEST TYPES
// ============================================================================

export interface CreateInvoiceLineItemRequest {
  description: string;
  quantity: number;
  unit: string;
  unitRate: number;
  costCodeId?: string;
  costCode?: string;
  taskId?: string;
  timesheetEntryIds?: string[];
  notes?: string;
}

export interface UpdateInvoiceLineItemRequest extends Partial<CreateInvoiceLineItemRequest> {
  _id?: string;
}

export interface CreateInvoiceRequest {
  invoiceType: InvoiceType;
  title?: string;
  clientId?: string;
  clientName: string;
  clientEmail?: string;
  clientAddress?: string;
  clientPhone?: string;
  lineItems: CreateInvoiceLineItemRequest[];
  invoiceDate?: string;
  dueDate?: string;
  taxRate?: number;
  discount?: number;
  discountType?: DiscountType;
  currency?: string;
  paymentTerms?: PaymentTerms;
  notes?: string;
  terms?: string;
  internalNotes?: string;
}

export interface UpdateInvoiceRequest extends Omit<Partial<CreateInvoiceRequest>, 'lineItems'> {
  lineItems?: UpdateInvoiceLineItemRequest[];
}

export interface SendInvoiceRequest {
  recipientEmail: string;
  message?: string;
  includePdf?: boolean;
}

export interface RecordPaymentRequest {
  amount: number;
  paymentDate: string;
  paymentMethod: PaymentMethod;
  referenceNumber?: string;
  notes?: string;
}

export interface GenerateFromTimesheetRequest {
  timesheetId: string;
  clientName?: string;
  clientEmail?: string;
  clientAddress?: string;
  clientId?: string;
  title?: string;
  invoiceDate?: string;
  dueDate?: string;
  taxRate?: number;
  discount?: number;
  discountType?: DiscountType;
  paymentTerms?: PaymentTerms;
  notes?: string;
  terms?: string;
}

export interface VoidCancelRequest {
  reason?: string;
}

// ============================================================================
// LIST / QUERY PARAMS
// ============================================================================

export interface InvoiceListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: InvoiceStatus;
  invoiceType?: InvoiceType;
  clientId?: string;
  clientName?: string;
  startDate?: string;
  endDate?: string;
  minTotal?: number;
  maxTotal?: number;
  overdue?: boolean;
  sortBy?: 'createdAt' | 'invoiceDate' | 'dueDate' | 'total' | 'invoiceNumber' | 'status';
  sortOrder?: 'asc' | 'desc';
}

// ============================================================================
// RESPONSE SHAPES
// ============================================================================

export interface InvoiceListResponse {
  invoices: Invoice[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  summary: {
    total: number;
    totalAmount: number;
    byStatus: Record<InvoiceStatus, { count: number; amount: number }>;
  };
}

export interface InvoiceStats {
  totalInvoiced: number;
  collected: number;
  outstanding: number;
  overdueAmount: number;
  avgDaysToPayment: number;
  invoiceCount: number;
  overdueCount: number;
  draftCount: number;
  sentCount: number;
}

export interface AgingBucket {
  label: string;
  count: number;
  amount: number;
}

export interface AgingSummary {
  current: AgingBucket;
  days1to30: AgingBucket;
  days31to60: AgingBucket;
  days61to90: AgingBucket;
  over90: AgingBucket;
  total: { count: number; amount: number };
}

// ============================================================================
// DISPLAY HELPERS
// ============================================================================

export const INVOICE_TYPE_LABELS: Record<InvoiceType, string> = {
  time_and_materials: 'Time & Materials',
  fixed_price: 'Fixed Price',
  service: 'Service',
  progress: 'Progress Billing',
  final: 'Final Invoice',
};

export const PAYMENT_TERMS_LABELS: Record<PaymentTerms, string> = {
  net_30: 'Net 30',
  net_45: 'Net 45',
  net_60: 'Net 60',
  due_on_receipt: 'Due on Receipt',
  custom: 'Custom',
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  check: 'Check',
  ach: 'ACH',
  wire: 'Wire Transfer',
  credit_card: 'Credit Card',
  cash: 'Cash',
  other: 'Other',
};
